import React from 'react'
import { FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import MultiSelect from '../../ui/MultiSelect'
import type { TeamMemberRole, TeamMemberStatus } from '../../../types/teamMember'

const ROLE_OPTIONS: TeamMemberRole[] = ['admin', 'user', 'operator']
const STATUS_OPTIONS: TeamMemberStatus[] = ['active', 'inactive']

function roleLabel(role: TeamMemberRole): string {
  if (role === 'admin') return 'Admin'
  if (role === 'user') return 'User'
  return 'Operator'
}

function statusLabel(status: TeamMemberStatus): string {
  return status === 'active' ? 'Active' : 'Inactive'
}

interface TeamMemberFiltersProps {
  search: string
  onSearchChange: (v: string) => void
  role: string
  onRoleChange: (v: string) => void
  status: string
  onStatusChange: (v: string) => void
}

export default function TeamMemberFilters({
  search,
  onSearchChange,
  role,
  onRoleChange,
  status,
  onStatusChange
}: TeamMemberFiltersProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-gray-700">
        <FunnelIcon className="h-4 w-4 text-gray-500" aria-hidden />
        Filters
      </div>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="min-w-0 flex-1">
          <label className="sr-only" htmlFor="team-member-search">
            Search by name, email, or member ID
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              id="team-member-search"
              className="input-primary pl-9 text-sm"
              placeholder="Search by name, email, or member ID…"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>
        <div className="w-full lg:w-56">
          <label className="mb-1 block text-xs font-medium text-gray-500">Filter by Role</label>
          <MultiSelect
            values={role === 'admin' || role === 'user' || role === 'operator' ? [role] : []}
            options={[...ROLE_OPTIONS]}
            maxSelections={1}
            getOptionLabel={(v) => roleLabel(v as TeamMemberRole)}
            getTriggerLabel={(sel) => roleLabel(sel[0] as TeamMemberRole)}
            placeholder="All roles"
            searchPlaceholder="Search roles…"
            onChange={(sel) => {
              const v = sel[0]
              onRoleChange(v === 'admin' || v === 'user' || v === 'operator' ? v : '')
            }}
          />
        </div>
        <div className="w-full lg:w-52">
          <label className="mb-1 block text-xs font-medium text-gray-500">Filter by Status</label>
          <MultiSelect
            values={status === 'active' || status === 'inactive' ? [status] : []}
            options={[...STATUS_OPTIONS]}
            maxSelections={1}
            getOptionLabel={(v) => statusLabel(v as TeamMemberStatus)}
            getTriggerLabel={(sel) => statusLabel(sel[0] as TeamMemberStatus)}
            placeholder="All statuses"
            searchPlaceholder="Search status…"
            onChange={(sel) => {
              const v = sel[0]
              onStatusChange(v === 'active' || v === 'inactive' ? v : '')
            }}
          />
        </div>
      </div>
    </div>
  )
}
